import React from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { ACOES_RAPIDAS, type AcaoRapidaLink } from './constants';
import { ArrowRightIcon } from './ArrowRightIcon';
import { EditItemButton } from './EditItemButton';
import { StarIcon } from './StarIcon';
import { TITULO_FAIXA } from './typography';

export interface AcoesRapidasData {
  title?: string;
  links?: Array<{
    label?: string;
    href?: Array<{ '@id': string }>;
    external?: boolean;
  }>;
}

/**
 * Visual do estado ativo (hover/foco) do botão de ação e do seu badge de seta.
 * É a única fonte desse visual: no desktop (`lg`+) ele só aparece no hover/foco;
 * no mobile (< `md`) é o estado padrão. Alterar aqui muda os dois.
 */
const LINK_ATIVO =
  'bg-blue-800 text-blue-50 ring-1 ring-inset ring-blue-900 dark:bg-[color:var(--surface)] dark:text-night-50 dark:ring-0';
const BADGE_ATIVO =
  'translate-x-0 bg-brand-600 text-blue-50 opacity-100 dark:bg-white dark:text-[color:var(--surface)]';

/** Id do painel, referenciado pelo `aria-controls` do cabeçalho. */
const PAINEL_ID = 'capa-acoes-painel';

/**
 * Card "O que você deseja fazer hoje?" (bloco `mpfAcoesRapidas`).
 *
 * Sem `data`, cai nos serviços reais de `ACOES_RAPIDAS`; com `data`, a lista do
 * bloco substitui a estática por inteiro (não há mescla — a redação decide o
 * conjunto todo).
 *
 * Abaixo de `md` (768px) a lista nasce recolhida atrás do próprio cabeçalho,
 * como a barra "Mais destaques" ao lado: oito serviços empilhados custavam uma
 * tela inteira de rolagem antes do resto da capa. De `md` para cima nada muda.
 */
export const AcoesRapidasCard: React.FC<{
  data?: AcoesRapidasData;
  /** Presente só no editor: abre a ação de índice N na barra lateral. */
  onEditItem?: (index: number) => void;
}> = ({ data, onEditItem }) => {
  const [aberto, setAberto] = React.useState(false);

  /**
   * A partir de `md` o painel está sempre visível por CSS, e quem decide isso é
   * o `md:flex` — não o estado. Sem esta sincronia o cabeçalho continuaria
   * anunciando "recolhido" no desktop com a lista à vista, e ainda gastaria uma
   * parada do Tab para um controle que não controla nada.
   *
   * Roda em efeito, depois da hidratação: ler o viewport no render faria o
   * servidor e o cliente discordarem. Sem listener de resize de propósito — no
   * caminho inverso (desktop → mobile) fechar o painel apagaria a escolha de
   * quem o tivesse aberto.
   */
  const [noDesktop, setNoDesktop] = React.useState(false);
  React.useEffect(() => {
    setNoDesktop(window.matchMedia('(min-width: 768px)').matches);
  }, []);

  const title = data?.title || ACOES_RAPIDAS.title;
  // Só a lista curada tem índice em `data.links`; a estática não é editável.
  const isCurated = !!(data?.links && data.links.length > 0);
  const links: AcaoRapidaLink[] = isCurated
    ? data!.links!.map((link, index) => ({
        id: `acao-${index}`,
        label: link.label ?? '',
        href: link.href?.[0]?.['@id'] ?? '',
        external: link.external,
      }))
    : ACOES_RAPIDAS.links;
  const showEdit = isCurated && !!onEditItem;

  return (
    /* Medidas do Figma: padding 16, raio 16. A moldura usa o mesmo traço dos
       divisores da lista, em vez da sombra do arquivo.
       `not-prose` porque a seção também é usada como bloco numa página comum,
       onde o `prose` do corpo do conteúdo vazaria para dentro — a explicação
       inteira está em `AcompanheRedesSection`. */
    <section
      aria-labelledby="capa-acoes-titulo"
      className="not-prose flex h-full flex-col rounded-2xl border border-gray-200 bg-surface-container-lowest p-4 dark:border-night-850 dark:bg-night-800"
    >
      {/* Semibold no `--blue-800` (#1E429F), o mesmo azul dos links logo
          abaixo (Templates aprovados, bloco 3619:75483).

          O `<button>` vai DENTRO do `<h3>`, e não ao lado: é o padrão de
          cabeçalho de accordion do WAI-ARIA. Assim o `id` continua no heading
          (a `<section>` o referencia em `aria-labelledby`) e o nível do título
          é o mesmo no mobile e no desktop — quem alterna é o botão. */}
      <h3
        id="capa-acoes-titulo"
        className={`mb-0 ${TITULO_FAIXA} text-blue-800 dark:text-white`}
      >
        <button
          type="button"
          aria-expanded={aberto || noDesktop}
          aria-controls={PAINEL_ID}
          // No desktop o botão não comanda nada: sai do Tab e ignora o clique.
          tabIndex={noDesktop ? -1 : undefined}
          onClick={() => setAberto((valor) => !valor)}
          /* `!bg-transparent !p-0 border-0 text-inherit`: o Semantic UI pinta
             todo `button` com fundo cinza e texto escuro próprios, e a regra
             dele ganha da classe utilitária sozinha — sem isso o cabeçalho
             vira um botão cinza no lugar do título azul. */
          className="group flex w-full items-center gap-2 rounded-lg border-0 !bg-transparent !p-0 text-left text-inherit shadow-none [font:inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 md:pointer-events-none md:cursor-default"
        >
          <StarIcon className="h-5 w-5 shrink-0 text-amber-400" />
          <span className="flex-1">{title}</span>
          <CaretDownIcon
            aria-hidden="true"
            className="h-5 w-5 shrink-0 text-blue-800 transition-transform group-aria-expanded:rotate-180 motion-reduce:transition-none md:hidden dark:text-white"
          />
        </button>
      </h3>

      {/* Recolhido só abaixo de `md`: a variante responsiva é emitida depois da
          utilitária sem prefixo, então `md:flex` vence o `hidden` do estado e
          o desktop nunca depende do que o React calculou.

          A régua âmbar mora aqui dentro — decorativa, e sem lista embaixo não
          teria o que separar. */}
      <div
        id={PAINEL_ID}
        className={`${aberto ? 'flex' : 'hidden'} flex-1 flex-col md:flex`}
      >
        <div
          className="mt-4 h-0.5 w-full bg-amber-400 dark:!bg-night-750"
          aria-hidden="true"
        />

        {/* `divide-y` resolve o divisor entre linhas sem sobrar um após a última.
          Os divisores vão de ponta a ponta do conteúdo e o recuo de 16px do
          texto fica no `<a>`, como no design.

          `flex-1` na lista e em cada item: no design os itens são
          `flex: 1 0 0` dentro de um card `align-self: stretch`, ou seja o card
          acompanha a altura da coluna de destaques e as 8 linhas repartem esse
          espaço em partes iguais. Sem isso cada linha teria a altura do próprio
          conteúdo e um rótulo que quebrasse em duas linhas desalinharia o
          conjunto. O `min-h` é o piso para quando não há coluna vizinha para
          esticar (mobile). */}
        <ul className="mt-4 grid grid-cols-1 gap-2 md:mt-2 md:grid-cols-2 md:gap-x-4 md:gap-y-0 md:border-t md:border-gray-200 md:dark:border-night-850 lg:mt-0 lg:flex lg:flex-1 lg:flex-col lg:gap-0 lg:border-t-0 lg:divide-y lg:divide-gray-200 lg:dark:divide-night-850">
          {links.map((link, index) => (
            <li
              key={link.id}
              className="flex min-w-0 md:items-center md:border-b md:border-gray-200 md:dark:border-night-850 lg:flex-1 lg:border-b-0"
            >
              {/* `pr-16` reserva o espaço da seta o tempo todo: sem isso o texto
                se mexeria quando ela aparecesse. Mesma mecânica do
                `.mpf-footer-channel-button` (global/_footer.scss).

                Cores do Footer_button `lg-light` do Figma (2965:35353, AB#11502):
                texto `blue-800` em repouso; no hover/foco caixa `blue-800`
                com contorno de 1px `blue-900` por dentro (`ring-inset`, para
                não mexer na altura) e texto `blue-50`; o badge da seta vira
                `brand-600` (#09024E) com a seta clara. Não é a cor primária
                (#0B0261) que o portal usava. `md:ring-0` desliga o contorno
                que `LINK_ATIVO` liga no mobile, onde ele é o estado padrão. */}
              <MPFLink
                href={link.href}
                openInNewTab={link.external || undefined}
                className={`group relative flex min-h-[4rem] min-w-0 flex-1 items-center rounded-md py-2 pl-3 pr-12 text-sm font-normal transition-colors md:pl-4 md:pr-16 md:text-base ${LINK_ATIVO} md:bg-transparent md:text-blue-800 md:ring-0 md:dark:bg-transparent md:dark:text-night-100 hover:bg-blue-800 hover:text-blue-50 hover:ring-1 hover:ring-inset hover:ring-blue-900 focus-visible:bg-blue-800 focus-visible:text-blue-50 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-900 focus-visible:outline-none dark:hover:bg-[color:var(--surface)] dark:hover:text-night-50 dark:focus-visible:bg-[color:var(--surface)] dark:focus-visible:text-night-50`}
              >
                {link.label}

                {/* Nasce deslocada para dentro do botão e desliza até a borda no
                  hover/foco. Com `prefers-reduced-motion` só o fade acontece.

                  A transição é escopada a `opacity` e `transform`, e não o
                  `transition` genérico do Tailwind: é o mesmo par de
                  propriedades (e o mesmo 0.3s ease-out) do
                  `.mpf-footer-channel-button__arrow`. O atalho genérico também
                  animaria cor, sombra e filtro, dando um tempo diferente do
                  rodapé quando qualquer uma delas mudasse junto. */}
                <span
                  aria-hidden="true"
                  className={`absolute right-2 top-1/2 flex h-[34px] w-[34px] -translate-y-1/2 items-center justify-center rounded transition-[opacity,transform] duration-300 ease-out ${BADGE_ATIVO} md:-translate-x-16 md:opacity-0 md:group-hover:translate-x-0 md:group-hover:opacity-100 md:group-focus-visible:translate-x-0 md:group-focus-visible:opacity-100 motion-reduce:translate-x-0 motion-reduce:transition-opacity`}
                >
                  <ArrowRightIcon className="h-5 w-5" />
                </span>
              </MPFLink>

              {/* O canto direito do `<a>` já é do badge da seta, então o lápis
                fica fora dele, como irmão na linha. */}
              {showEdit && (
                <EditItemButton
                  label={`Editar ação: ${link.label || 'sem rótulo'}`}
                  onClick={() => onEditItem!(index)}
                  className="ml-2"
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default AcoesRapidasCard;
